/**
 * admin/src/hooks/useBlog.ts
 * Hooks cho Blog management
 *
 * useBlogList   — danh sách bài viết (từ blog-index.json qua R2)
 * useBlogPost   — 1 bài detail (frontmatter + markdown body)
 * useBlogMut    — POST / PUT / DELETE + rebuild index
 */

import { useState, useEffect, useCallback } from 'react';
import { adminFetch } from './useAdmin';
import { toast } from '../components/ui/index';
import type {
  AdminBlogPost,
  AdminBlogDetail,
  PaginatedResponse,
} from '../types/admin';

// ─── Params ───────────────────────────────────────────────────────────────────
export interface BlogListParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
}

interface UseBlogListReturn {
  posts: AdminBlogPost[];
  total: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBlogList(params: BlogListParams = {}): UseBlogListReturn {
  const [data, setData] = useState<PaginatedResponse<AdminBlogPost> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    adminFetch<PaginatedResponse<AdminBlogPost>>('/blog', {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        search: params.search || undefined,
        category: params.category || undefined,
      },
    })
      .then((res) => { if (!cancelled) { setData(res); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi tải blog');
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), tick]);

  return {
    posts: data?.data ?? [],
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 1,
    isLoading,
    error,
    refetch,
  };
}

// ─── Blog Post Detail ─────────────────────────────────────────────────────────
interface UseBlogPostReturn {
  post: AdminBlogDetail | null;
  isLoading: boolean;
  error: string | null;
}

export function useBlogPost(slug: string | undefined): UseBlogPostReturn {
  const [post, setPost] = useState<AdminBlogDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) { setIsLoading(false); return; }
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    adminFetch<AdminBlogDetail>(`/blog/${slug}`)
      .then((res) => { if (!cancelled) { setPost(res); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi tải bài viết');
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [slug]);

  return { post, isLoading, error };
}

// ─── Blog Mutations ───────────────────────────────────────────────────────────
interface CreatePostPayload {
  title: string;
  description?: string;
  category: string;
  body: string;
  slug?: string;
  author?: string;
  tags?: string[];
  cover_image?: string;
  reading_time?: number;
  is_featured?: boolean;
  tab?: string;
}

interface UpdatePostPayload extends Partial<CreatePostPayload> {
  is_published?: boolean;
  prev_post?: string;
  next_post?: string;
}

export function useBlogMutations(onSuccess?: () => void) {
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);

  async function createPost(payload: CreatePostPayload): Promise<string | null> {
    setIsSaving(true);
    try {
      const res = await adminFetch<{ ok: boolean; slug: string }>('/blog', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      toast(`Đã tạo bài "${payload.title}"`, 'success');
      onSuccess?.();
      return res.slug;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi tạo bài', 'error');
      return null;
    } finally {
      setIsSaving(false);
    }
  }

  async function updatePost(slug: string, payload: UpdatePostPayload): Promise<boolean> {
    setIsSaving(true);
    try {
      await adminFetch(`/blog/${slug}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      toast('Đã lưu bài viết', 'success');
      onSuccess?.();
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi cập nhật bài', 'error');
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function deletePost(slug: string): Promise<boolean> {
    setIsDeleting(true);
    try {
      await adminFetch(`/blog/${slug}`, { method: 'DELETE' });
      toast(`Đã xóa bài "${slug}"`, 'success');
      onSuccess?.();
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi xóa bài', 'error');
      return false;
    } finally {
      setIsDeleting(false);
    }
  }

  async function rebuildIndex(): Promise<boolean> {
    setIsRebuilding(true);
    try {
      const res = await adminFetch<{ ok: boolean; total: number; message: string }>(
        '/blog/index/rebuild',
        { method: 'POST' }
      );
      toast(res.message ?? `Đã rebuild index (${res.total} bài)`, 'success');
      onSuccess?.();
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi rebuild index', 'error');
      return false;
    } finally {
      setIsRebuilding(false);
    }
  }

  return { createPost, updatePost, deletePost, rebuildIndex, isSaving, isDeleting, isRebuilding };
}
